import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';
import type { LauncherPosition } from '@features/front-chat/types';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Paper,
  Portal,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import type { SupportDraft, SupportInteraction, SupportView } from '../types';

import { JotCharacter } from './JotCharacter';
import { SupportComposer } from './SupportComposer';

export interface JotSupportDisplayProps {
  launcherVisible?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  launcherPosition?: LauncherPosition;
  /** Optional companion content anchored to this widget's single launcher. */
  renderLauncherAccessory?: (anchor: {
    launcher: RefObject<HTMLButtonElement | null>;
    side: LauncherPosition['side'];
    bottomOffset: string;
  }) => ReactNode;
}

export interface JotSupportWidgetProps extends JotSupportDisplayProps {
  replyEmail: string;
  preparationError?: string;
  view?: SupportView;
  draft: SupportDraft;
  onDraftChange: (draft: SupportDraft) => void;
  onSubmit: () => void;
  onRetry?: () => void;
  onCheckStatus?: () => void;
  onNewRequest?: () => void;
  checkingStatus?: boolean;
  initiallyOpen?: boolean;
  reducedMotion?: boolean;
  onInteraction?: (event: SupportInteraction) => void;
}

const receiptCopy = {
  sending: ['Sending…', ''],
  sent: ['Sent', 'Our team will reply by email.'],
  failure: [
    'Your request couldn’t be delivered',
    'Please email our support team so we can help. Your draft is saved here.',
  ],
  'retryable-failure': [
    'Your request wasn’t sent',
    'Your message and screenshot are saved here. Try sending this same request again.',
  ],
  unknown: [
    'We can’t confirm delivery yet',
    'Your request may have reached support. Check its status before sending another request.',
  ],
} as const;

export function JotSupportWidget({
  replyEmail,
  preparationError,
  view = 'compose',
  draft,
  onDraftChange,
  onSubmit,
  onRetry,
  onCheckStatus,
  onNewRequest,
  checkingStatus = false,
  initiallyOpen = false,
  reducedMotion = false,
  onInteraction,
  launcherVisible = true,
  open: controlledOpen,
  onOpenChange,
  launcherPosition,
  renderLauncherAccessory,
}: JotSupportWidgetProps) {
  const [internalOpen, setInternalOpen] = useState(initiallyOpen);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const characterWidth = theme.spacing(isMobile ? 9 : 11.5);
  const characterHeight = theme.spacing(isMobile ? 9.75 : 12.5);
  const bottom = launcherPosition ? theme.spacing(launcherPosition.bottom) : theme.spacing(1);
  const side = launcherPosition?.side ?? 'left';
  const panelBottom = `calc(${bottom} + ${characterHeight} + ${theme.spacing(1.25)})`;
  const [reaction, setReaction] = useState(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const focusPanelOnMount = useCallback((element: HTMLDivElement | null) => {
    panel.current = element;
    // The composer focuses its message. Receipt-only panels focus their container.
    if (element && !element.contains(document.activeElement))
      element.focus({ preventScroll: true });
  }, []);
  const titleId = useId();
  const dialogId = useId();
  const close = () => {
    setOpen(false);
    onInteraction?.('closed');
    launcher.current?.focus({ preventScroll: true });
  };
  const handleEscape = (event: KeyboardEvent<HTMLElement>) => {
    if (open && event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };
  const mood = !open ? 'idle' : view === 'sending' ? 'sending' : view === 'sent' ? 'sent' : 'idle';
  return (
    <>
      {launcherVisible && (
        <ButtonBase
          ref={launcher}
          data-jot-launcher
          type="button"
          disableRipple
          aria-label="Contact support"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? dialogId : undefined}
          onKeyDown={handleEscape}
          onClick={() => {
            if (open) close();
            else {
              setReaction(value => value + 1);
              setOpen(true);
              onInteraction?.('opened');
            }
          }}
          sx={{
            // Keep the floating character compact using the application spacing scale.
            position: 'fixed',
            [side]: theme.spacing(0.5),
            bottom,
            width: characterWidth,
            height: characterHeight,
            zIndex: theme => theme.zIndex.modal - 1,
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            boxShadow: 'none',
            borderRadius: 2,
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <JotCharacter
            mood={mood}
            reaction={reaction}
            reducedMotion={reducedMotion}
            size={characterWidth}
            height={characterHeight}
          />
        </ButtonBase>
      )}
      {launcherVisible &&
        !open &&
        renderLauncherAccessory?.({ launcher, side, bottomOffset: panelBottom })}
      {open && (
        <Portal>
          <Paper
            ref={focusPanelOnMount}
            id={dialogId}
            className="rr-block"
            role="dialog"
            aria-modal={false}
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleEscape}
            elevation={8}
            sx={{
              position: 'fixed',
              [side]: '16px',
              bottom: panelBottom,
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: `calc(100dvh - ${bottom} - ${characterHeight} - ${theme.spacing(3.25)})`,
              zIndex: theme => theme.zIndex.modal - 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              outline: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ p: 2, pl: 2.5, alignItems: 'center', flexShrink: 0 }}
            >
              <MailOutlineRounded color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography id={titleId} component="h2" variant="h6">
                  Contact support
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  A message to the JotPsych team
                </Typography>
              </Box>
              <IconButton onClick={close} aria-label="Close support">
                <CloseRounded />
              </IconButton>
            </Stack>
            <Divider />
            <Box sx={{ p: 2.5, overflowY: 'auto', minHeight: 0 }}>
              {preparationError && view === 'compose' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {preparationError}
                </Alert>
              )}
              {view === 'compose' ? (
                <SupportComposer
                  draft={draft}
                  onChange={onDraftChange}
                  replyEmail={replyEmail}
                  locked={false}
                  onSubmit={() => {
                    panel.current?.focus({ preventScroll: true });
                    onInteraction?.('submitted');
                    onSubmit();
                  }}
                  onAttachmentSelected={() => onInteraction?.('attachment_selected')}
                />
              ) : (
                <Stack spacing={2.5} sx={{ py: 1, textAlign: 'center', alignItems: 'center' }}>
                  <Box
                    sx={{
                      alignSelf: 'flex-end',
                      maxWidth: '90%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '16px 16px 4px 16px',
                      p: 1.5,
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                    >
                      {draft.message}
                    </Typography>
                    {draft.screenshot && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, display: 'block', overflowWrap: 'anywhere' }}
                      >
                        Screenshot: {draft.screenshot.name}
                      </Typography>
                    )}
                  </Box>
                  <Box role="status" aria-live="polite" aria-atomic="true">
                    <Typography
                      variant={view === 'sending' ? 'body2' : 'h6'}
                      component="h3"
                      sx={{ mb: 1 }}
                    >
                      {receiptCopy[view][0]}
                    </Typography>
                    {receiptCopy[view][1] && (
                      <Typography variant="body2" color="text.secondary">
                        {receiptCopy[view][1]}
                      </Typography>
                    )}
                  </Box>
                  {view === 'sent' && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CheckCircleOutlineRounded color="success" fontSize="small" />
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {replyEmail}
                      </Typography>
                    </Stack>
                  )}
                  {view === 'retryable-failure' && onRetry && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        panel.current?.focus({ preventScroll: true });
                        onInteraction?.('retry_clicked');
                        onRetry();
                      }}
                    >
                      Try again
                    </Button>
                  )}
                  {view === 'unknown' && onCheckStatus && (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={checkingStatus}
                      onClick={() => {
                        onInteraction?.('status_requested');
                        onCheckStatus();
                      }}
                    >
                      {checkingStatus ? 'Checking status…' : 'Check delivery status'}
                    </Button>
                  )}
                  {(view === 'unknown' || view === 'failure') && (
                    <Button
                      href="mailto:support@jotpsych.com?subject=Support%20request"
                      startIcon={<MailOutlineRounded />}
                      size="small"
                    >
                      Email support
                    </Button>
                  )}
                  {view === 'sent' && (
                    <Button variant="contained" fullWidth onClick={close}>
                      Done
                    </Button>
                  )}
                  {view === 'sent' && onNewRequest && (
                    <Button
                      size="small"
                      onClick={() => {
                        panel.current?.focus({ preventScroll: true });
                        onNewRequest();
                      }}
                    >
                      Start another request
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          </Paper>
        </Portal>
      )}
    </>
  );
}
